import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowUpRight, Brain, Link2, MessageCircle, MessagesSquare, Plus, Search, Trash2, Unlink, Workflow } from "lucide-react";
import {
  getPlatformAgents,
  getAvailableAgents,
  addAgentToPlatform,
  removeAgentFromPlatform,
} from "../../../services/native-client-platform";
import { toast } from "sonner";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Badge } from "../../../components/ui/badge";
import { Card } from "../../../components/ui/card";
import { ConfirmDialog } from "../../../components/ui/dialog";
import { EmptyState } from "../../../components/ui/empty-state";
import { Spinner } from "../../../components/ui/spinner";
import { Tooltip } from "../../../components/ui/tooltip";
import useDebounce from "../../../hooks/useDebounce";
import { cn } from "../../../lib/cn";

const getAgentField = (agent, field) =>
  agent?.[field] ?? agent?.assistant?.[field] ?? agent?.chatbot?.[field];

const getAssistantId = (agent) =>
  agent?.assistant?.id ?? agent?.assistantID ?? null;

const getAgentMetric = (agent, field) =>
  Number(agent?.[field] ?? agent?.assistant?.[field] ?? 0) || 0;

const formatMetric = (value) => new Intl.NumberFormat("pt-BR").format(value);

export default function AgentsTab({ platformId, companyId }) {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [associated, setAssociated] = useState([]);
  const [associatedLoading, setAssociatedLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [confirmRemoveTarget, setConfirmRemoveTarget] = useState(null);
  const debouncedSearch = useDebounce(searchTerm, 350);

  const associatedIds = useMemo(
    () => new Set(associated.map((a) => a.id)),
    [associated]
  );

  const usageTotals = useMemo(
    () => associated.reduce(
      (totals, agent) => {
        if (!getAssistantId(agent)) return totals;
        return {
          messages: totals.messages + getAgentMetric(agent, "messagesInMonth"),
          conversations: totals.conversations + getAgentMetric(agent, "conversationsInMonth"),
        };
      },
      { messages: 0, conversations: 0 }
    ),
    [associated]
  );

  const fetchAssociated = useCallback(async () => {
    if (!platformId) return;
    setAssociatedLoading(true);
    try {
      const { data } = await getPlatformAgents(platformId);
      setAssociated(data || []);
    } catch (e) {
      console.error(e);
      toast.error("Falha ao carregar agentes vinculados");
    } finally {
      setAssociatedLoading(false);
    }
  }, [platformId]);

  const fetchSearchResults = useCallback(
    async () => {
      if (!platformId) return;
      setSearchLoading(true);
      try {
        const { data } = await getAvailableAgents(platformId);
        setSearchResults(data || []);
      } catch (e) {
        console.error(e);
        toast.error("Falha ao buscar agentes");
      } finally {
        setSearchLoading(false);
      }
    },
    [platformId]
  );

  useEffect(() => {
    fetchAssociated();
  }, [fetchAssociated]);

  useEffect(() => {
    fetchSearchResults();
  }, [fetchSearchResults]);

  const handleAdd = useCallback(
    async (assistant) => {
      if (associatedIds.has(assistant.id)) return;
      setActionLoading({ type: "add", id: assistant.id });
      try {
        await addAgentToPlatform(Number(platformId), Number(assistant.id));
        const name = getAgentField(assistant, "name") ?? "";
        toast.success(`${name} vinculado com sucesso`);
        await fetchAssociated();
      } catch (e) {
        console.error(e);
        toast.error(
          e?.data?.message
            ? `Falha ao vincular: ${e.data.message}`
            : "Falha ao vincular assistente"
        );
      } finally {
        setActionLoading(null);
      }
    },
    [platformId, associatedIds, fetchAssociated]
  );

  const handleRemove = useCallback(async () => {
    if (!confirmRemoveTarget) return;
    setActionLoading({ type: "remove", id: confirmRemoveTarget.id });
    try {
      await removeAgentFromPlatform(
        Number(platformId),
        Number(confirmRemoveTarget.id)
      );
      toast.success(`${confirmRemoveTarget.name ?? confirmRemoveTarget.assistant?.name ?? confirmRemoveTarget.chatbot?.name} desvinculado`);
      setConfirmRemoveTarget(null);
      await fetchAssociated();
    } catch (e) {
      console.error(e);
      toast.error(
        e?.data?.message
          ? `Falha ao desvincular: ${e.data.message}`
          : "Falha ao desvincular assistente"
      );
    } finally {
      setActionLoading(null);
    }
  }, [confirmRemoveTarget, platformId, fetchAssociated]);

  const availableResults = useMemo(
    () => searchResults.filter((a) => {
      if (debouncedSearch) {
        const name = getAgentField(a, "name") ?? "";
        if (!name.toLowerCase().includes(debouncedSearch.toLowerCase())) {
          return false;
        }
      }
      return !associatedIds.has(a.id);
    }),
    [searchResults, debouncedSearch, associatedIds]
  );

  return (
    <>
      <div className="flex flex-col gap-5">
        {/* Linked assistants section */}
        <section className="flex flex-col gap-2.5">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <SectionTitle
              icon={Link2}
              title="Agentes deste cliente"
              badge={associated.length}
            />
            <Button
              variant="primary"
              size="sm"
              leftIcon={Plus}
              onClick={() => navigate(`/onboarding?flow=agent&clientId=${platformId}`)}
            >
              Criar agente
            </Button>
          </div>
          <p className="text-xs leading-relaxed" style={{ color: "var(--dash-text-muted)" }}>
            Cada agente tem os próprios canais de atendimento. Um canal nunca é compartilhado entre agentes.
          </p>

          {!associatedLoading && associated.length > 0 && (
            <UsageSummary
              messages={usageTotals.messages}
              conversations={usageTotals.conversations}
            />
          )}

          {associatedLoading ? (
            <div className="flex flex-col gap-2">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="flex animate-pulse items-center gap-3 rounded-xl p-3" style={{ background: "var(--dash-surface-subtle)", border: "1px solid var(--dash-border)", animationDelay: `${i * 80}ms` }}>
                  <div className="h-9 w-9 rounded-lg" style={{ background: "var(--dash-surface-hover)" }} />
                  <div className="flex flex-1 flex-col gap-1.5">
                    <div className="h-3.5 rounded" style={{ background: "var(--dash-surface-hover)", width: [140, 180, 120][i % 3] }} />
                    <div className="h-2.5 w-16 rounded" style={{ background: "var(--dash-surface-hover)" }} />
                  </div>
                  <div className="h-7 w-7 rounded-lg" style={{ background: "var(--dash-surface-hover)" }} />
                </div>
              ))}
            </div>
          ) : associated.length === 0 ? (
            <div
              className="rounded-xl px-4 py-6 text-center text-xs"
              style={{
                background: "var(--dash-surface-subtle)",
                border: "1px dashed var(--dash-border-strong)",
                color: "var(--dash-text-muted)",
              }}
            >
              Nenhum agente configurado ainda. Crie o primeiro agente para este
              cliente e conecte o canal de atendimento no fluxo guiado.
              <div className="mt-4 flex justify-center">
                <Button
                  variant="primary"
                  size="sm"
                  leftIcon={Plus}
                  onClick={() => navigate(`/onboarding?flow=agent&clientId=${platformId}`)}
                >
                  Criar primeiro agente
                </Button>
              </div>
            </div>
          ) : (
            <ul className="flex flex-col gap-2">
              {associated.map((assistant) => {
                const assistantId = getAssistantId(assistant);

                return (
                  <AssistantRow
                    key={assistant.id}
                    assistant={assistant}
                    action={
                      <div className="flex items-center gap-1.5">
                        {assistantId && (
                          <Button
                            variant="outline"
                            size="sm"
                            leftIcon={ArrowUpRight}
                            onClick={() => navigate(`/assistants/${assistantId}`)}
                          >
                            Abrir
                          </Button>
                        )}
                        {assistantId && (
                          <Button
                            variant="outline"
                            size="sm"
                            leftIcon={MessageCircle}
                            onClick={() => navigate(`/assistants/${assistantId}/integrations`)}
                          >
                            Canais
                          </Button>
                        )}
                        <Tooltip content="Desvincular">
                          <Button
                            variant="danger"
                            size="icon-sm"
                            onClick={() => setConfirmRemoveTarget(assistant)}
                            loading={
                              actionLoading?.type === "remove" &&
                              actionLoading?.id === assistant.id
                            }
                          >
                            <Unlink size={13} />
                          </Button>
                        </Tooltip>
                      </div>
                    }
                  />
                );
              })}
            </ul>
          )}
        </section>

        <div
          className="h-px w-full"
          style={{ background: "var(--dash-border)" }}
        />

        {/* Add assistant section */}
        <section className="flex flex-col gap-2.5">
          <SectionTitle icon={Plus} title="Vincular agente existente" />

          <p className="text-xs leading-relaxed" style={{ color: "var(--dash-text-muted)" }}>
            Use esta opção somente para trazer um agente já criado. Para uma nova configuração, use “Criar agente”.
          </p>

          <Input
            leftIcon={Search}
            placeholder="Buscar agente por nome..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />

          <div
            className="dash-scroll max-h-72 overflow-y-auto rounded-xl"
            style={{
              background: "var(--dash-surface-subtle)",
              border: "1px solid var(--dash-border)",
            }}
          >
            {searchLoading ? (
              <div className="flex items-center justify-center py-10">
                <Spinner size={18} />
              </div>
            ) : availableResults.length === 0 ? (
              <EmptyState
                icon={Brain}
                title={
                  debouncedSearch
                    ? "Nenhum resultado"
                    : "Sem agentes disponíveis"
                }
                description={
                  debouncedSearch
                    ? `Nenhum assistente encontrado para "${debouncedSearch}".`
                    : "Todos os agentes elegíveis já estão vinculados ou nenhum existe."
                }
              />
            ) : (
              <ul className="flex flex-col">
                {availableResults.map((assistant, i) => (
                  <li
                    key={assistant.id}
                    className={cn(
                      "px-3 py-2.5",
                      i !== availableResults.length - 1 && "border-b"
                    )}
                    style={{
                      borderColor:
                        i !== availableResults.length - 1
                          ? "var(--dash-border)"
                          : undefined,
                    }}
                  >
                    <AssistantRow
                      assistant={assistant}
                      variant="search"
                      action={
                        <Button
                          variant="primary"
                          size="sm"
                          leftIcon={Plus}
                          onClick={() => handleAdd(assistant)}
                          loading={
                            actionLoading?.type === "add" &&
                            actionLoading?.id === assistant.id
                          }
                        >
                          Vincular
                        </Button>
                      }
                    />
                  </li>
                ))}
              </ul>
            )}
          </div>
        </section>
      </div>

      <ConfirmDialog
        open={!!confirmRemoveTarget}
        onClose={() => setConfirmRemoveTarget(null)}
        onConfirm={handleRemove}
        title="Desvincular assistente?"
        description={
          confirmRemoveTarget
            ? `"${confirmRemoveTarget.name ?? confirmRemoveTarget.assistant?.name ?? confirmRemoveTarget.chatbot?.name}" deixará de estar vinculado a esta plataforma. A associação pode ser refeita depois.`
            : ""
        }
        confirmLabel="Desvincular"
        loading={
          actionLoading?.type === "remove" &&
          actionLoading?.id === confirmRemoveTarget?.id
        }
      />
    </>
  );
}

function SectionTitle({ icon: Icon, title, badge }) {
  return (
    <div className="flex items-center gap-2">
      <Icon size={14} style={{ color: "var(--dash-primary)" }} />
      <h3
        className="text-[11px] font-semibold uppercase tracking-[0.12em]"
        style={{ color: "var(--dash-text-soft)" }}
      >
        {title}
      </h3>
      {typeof badge === "number" && badge > 0 && (
        <Badge variant="primary">{badge}</Badge>
      )}
    </div>
  );
}

function UsageSummary({ messages, conversations }) {
  return (
    <div
      className="flex flex-wrap gap-2 rounded-xl p-3"
      style={{ background: "var(--dash-surface-subtle)", border: "1px solid var(--dash-border)" }}
    >
      <UsageMetric icon={MessagesSquare} value={messages} label="mensagens neste mês" />
      <UsageMetric icon={MessageCircle} value={conversations} label="conversas neste mês" />
    </div>
  );
}

function UsageMetric({ icon: Icon, value, label }) {
  return (
    <div className="flex items-center gap-2 rounded-lg px-2 py-1.5" style={{ background: "var(--dash-surface)" }}>
      <Icon size={13} style={{ color: "var(--dash-primary)" }} />
      <span className="text-sm font-semibold tabular-nums" style={{ color: "var(--dash-text)" }}>{formatMetric(value)}</span>
      <span className="text-[11px]" style={{ color: "var(--dash-text-muted)" }}>{label}</span>
    </div>
  );
}

function AssistantRow({ assistant, action, variant = "associated" }) {
  return (
    <div
      className={cn(
        "flex items-center gap-3",
        variant === "associated" && "rounded-xl p-3"
      )}
      style={
        variant === "associated"
          ? {
              background: "var(--dash-surface-subtle)",
              border: "1px solid var(--dash-border)",
            }
          : undefined
      }
    >
      <div
        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg"
        style={{
          background: "var(--dash-primary-soft)",
          border: "1px solid var(--dash-primary-border)",
          color: "var(--dash-primary)",
        }}
      >
        {assistant.chatbot ? <Workflow size={14} strokeWidth={2.2} /> : <Brain size={14} strokeWidth={2.2} />}
      </div>

      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <div className="flex items-center gap-2">
          <span
            className="truncate text-sm font-semibold"
            style={{ color: "var(--dash-text)" }}
          >
            {getAgentField(assistant, "name")}
          </span>
          {(getAgentField(assistant, "llmModel")) && (
            <Badge variant="outline">{getAgentField(assistant, "llmModel")}</Badge>
          )}
          {getAgentField(assistant, "isActive") === false && <Badge variant="muted">Inativo</Badge>}
        </div>
        {(getAgentField(assistant, "description")) && (
          <span
            className="truncate text-[11px]"
            style={{ color: "var(--dash-text-muted)" }}
          >
            {getAgentField(assistant, "description")}
          </span>
        )}
        {variant === "associated" && getAssistantId(assistant) && (
          <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px]" style={{ color: "var(--dash-text-muted)" }}>
            <span className="flex items-center gap-1">
              <MessagesSquare size={11} />
              {formatMetric(getAgentMetric(assistant, "messagesInMonth"))} mensagens
            </span>
            <span className="flex items-center gap-1">
              <MessageCircle size={11} />
              {formatMetric(getAgentMetric(assistant, "conversationsInMonth"))} conversas
            </span>
          </div>
        )}
      </div>

      <div className="shrink-0">{action}</div>
    </div>
  );
}
